import { Box } from "@mui/material";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { useEffect, useMemo, type ReactNode } from "react";

import { sanitizeRichText } from "./sanitize.js";

/**
 * Minimal rich-text editor used for ticket comments and any other
 * "short rich body" surface in the dashboard. Built on TipTap so it
 * shares the same engine as the bigger CRM `tiptap-editor.tsx` (no
 * second editor library to bundle), but keeps the toolbar to a tight
 * set: bold, italic, underline, ordered list, bullet list, link.
 *
 * The full-featured CRM version stays for email composition where
 * headings / colour / image uploads / drag-drop matter. This one is
 * intentionally smaller — comment threads should read like
 * conversation, not formatted documents.
 *
 * Output is HTML. Render via `<RichText html={…} />` (sibling
 * component) on the read side; that helper sanitises through
 * DOMPurify before injecting, so a malicious payload that bypasses
 * the editor (e.g. someone POSTing raw `<script>` to the API) can't
 * execute in another user's browser.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder = "Write a reply…",
  disabled = false,
  minHeightPx = 120,
  ariaLabel,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minHeightPx?: number;
  ariaLabel?: string;
}): JSX.Element | null {
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        bulletList: { HTMLAttributes: { class: "list-disc pl-5" } },
        orderedList: { HTMLAttributes: { class: "list-decimal pl-5" } },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer",
          // Plain marker class; the brand colour + underline are applied via
          // the wrapper Box's `sx` (`& a`) so this survives Tailwind removal.
          class: "rte-link",
        },
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
        class:
          "prose prose-sm max-w-none px-3 py-2 focus:outline-none [&_p]:my-1",
      },
    },
  });

  // Keep the editor in sync when `value` is reset externally (e.g.
  // after a successful submit). Don't clobber the editor while the
  // user is mid-typing.
  useEffect(() => {
    if (!editor) return;
    if (editor.isFocused) return;
    if (editor.getHTML() !== value) editor.commands.setContent(value);
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  const isEmpty = editor.isEmpty;

  return (
    <Box
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.default",
        "&:focus-within": { borderColor: "text.disabled" },
        // Rendered links inside the editor content (the Link extension tags
        // them `.rte-link`). Was a Tailwind `var(--color-brand)` class.
        "& a.rte-link": { color: "primary.main", textDecoration: "underline", textUnderlineOffset: "2px" },
      }}
    >
      <Toolbar editor={editor} disabled={disabled} />
      <Box sx={{ position: "relative", minHeight: minHeightPx }}>
        {isEmpty && !editor.isFocused && (
          <Box
            component="p"
            sx={{ pointerEvents: "none", position: "absolute", left: 12, top: 8, m: 0, fontSize: 14, color: "text.disabled" }}
          >
            {placeholder}
          </Box>
        )}
        {/* tiptap content region. The `prose` typography + list classes are
            part of the editor configuration (ProseMirror content styling),
            not chrome, so they stay as content classes. */}
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}

/**
 * Read-side counterpart — sanitises the HTML through DOMPurify and
 * renders the result. The allow-list mirrors what `RichTextEditor`
 * can produce: paragraphs, line breaks, bold/italic/underline marks,
 * lists, and external-link anchors. Anything else (script, style, on*
 * handlers) is stripped, so a malicious payload that bypassed the
 * editor and was POSTed straight to the API still can't execute in
 * another user's browser.
 */
export function RichText({
  html,
  className,
}: {
  html: string;
  className?: string;
}): JSX.Element {
  const safe = useMemo(() => sanitizeRichText(html), [html]);

  return (
    <Box
      {...(className ? { className } : {})}
      sx={{
        maxWidth: "none",
        fontSize: 14,
        "& p": { my: 0.5 },
        "& a": { color: "primary.main", textDecoration: "underline" },
      }}
      // The string we inject is the DOMPurify output, which strips any
      // tag / attribute not on the allow-list above. Source: comment
      // bodies coming back from the support-center API.
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

interface ToolbarProps {
  editor: ReturnType<typeof useEditor>;
  disabled: boolean;
}

function Toolbar({ editor, disabled }: ToolbarProps): JSX.Element | null {
  if (!editor) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 0.25,
        borderBottom: 1,
        borderColor: "divider",
        px: 0.75,
        py: 0.5,
      }}
    >
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive("bold")}
        disabled={disabled}
        label="Bold"
      >
        <Box component="span" sx={{ fontWeight: 700 }}>B</Box>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive("italic")}
        disabled={disabled}
        label="Italic"
      >
        <Box component="span" sx={{ fontStyle: "italic" }}>I</Box>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive("underline")}
        disabled={disabled}
        label="Underline"
      >
        <Box component="span" sx={{ textDecoration: "underline" }}>U</Box>
      </ToolbarButton>
      <Sep />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive("bulletList")}
        disabled={disabled}
        label="Bullet list"
      >
        ●
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive("orderedList")}
        disabled={disabled}
        label="Numbered list"
      >
        1.
      </ToolbarButton>
      <Sep />
      <ToolbarButton
        onClick={() => promptForLink(editor)}
        active={editor.isActive("link")}
        disabled={disabled}
        label="Insert link"
      >
        🔗
      </ToolbarButton>
    </Box>
  );
}

function Sep(): JSX.Element {
  return <Box component="span" sx={{ mx: 0.25, height: 16, width: "1px", bgcolor: "divider" }} />;
}

function ToolbarButton({
  onClick,
  active,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        height: 24,
        minWidth: 24,
        borderRadius: 1,
        px: 0.75,
        fontSize: "0.75rem",
        border: 0,
        cursor: disabled ? "default" : "pointer",
        background: "transparent",
        opacity: disabled ? 0.5 : 1,
        "&:focus-visible": {
          outline: (t) => `2px solid ${t.palette.primary.main}`,
          outlineOffset: 1,
        },
        ...(active
          ? { bgcolor: "action.selected", color: "primary.main" }
          : {
              color: "text.disabled",
              "&:hover": { bgcolor: "action.hover", color: "text.primary" },
            }),
      }}
    >
      {children}
    </Box>
  );
}

function promptForLink(editor: NonNullable<ReturnType<typeof useEditor>>) {
  const previous = editor.getAttributes("link").href as string | undefined;
  // eslint-disable-next-line no-alert
  const url = window.prompt("Enter the URL:", previous ?? "https://");
  if (url === null) return;
  if (url === "") {
    editor.chain().focus().unsetLink().run();
    return;
  }
  const { from, to } = editor.state.selection;
  if (from === to) {
    // No selection — insert URL as link text.
    editor.chain().focus().insertContent(`<a href="${url}">${url}</a>`).run();
  } else {
    editor.chain().focus().setLink({ href: url }).run();
  }
}
